/**
 * ReplayProgressBar Component
 *
 * Instagram Stories-style segmented progress bar for the replay UI.
 * Shows current position in a sequence of moments or edits.
 *
 * Visual design:
 * - Horizontal bar divided into equal-width segments
 * - Filled segments (past), current segment (highlighted), empty segments (future)
 * - Hover reveals segment number tooltip
 * - Click jumps to that segment
 * - Keyboard navigation with arrow keys
 *
 * @example
 * ```tsx
 * // Top level (28 moments):
 * <ReplayProgressBar
 *   total={28}
 *   current={11}
 *   onSegmentClick={setCurrentMoment}
 *   label="Moment 12 of 28"
 * />
 *
 * // Drilled down (8 edits within moment):
 * <ReplayProgressBar
 *   total={8}
 *   current={2}
 *   onSegmentClick={setCurrentEdit}
 *   label="Edit 3 of 8 in this moment"
 * />
 * ```
 */

import { useState, useCallback, useRef, useEffect, useId } from "react";

export interface ReplayProgressBarProps {
  /** Total number of segments */
  total: number;
  /** Current position (0-indexed) */
  current: number;
  /** Called when user clicks a segment */
  onSegmentClick: (index: number) => void;
  /** Optional label below bar */
  label?: string;
  /** Optional className */
  className?: string;
  /** Test ID for the component */
  testId?: string;
}

export function ReplayProgressBar({
  total,
  current,
  onSegmentClick,
  label,
  className = "",
  testId = "replay-progress-bar",
}: ReplayProgressBarProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const segmentRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tooltipIdPrefix = useId();

  // Clamp current to valid range (safe even if total <= 0)
  const clampedCurrent = total > 0 ? Math.max(0, Math.min(total - 1, current)) : 0;

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (total <= 0) return;

      let nextIndex: number | null = null;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          nextIndex = Math.max(0, index - 1);
          break;
        case "ArrowRight":
          e.preventDefault();
          nextIndex = Math.min(total - 1, index + 1);
          break;
        case "Home":
          e.preventDefault();
          nextIndex = 0;
          break;
        case "End":
          e.preventDefault();
          nextIndex = total - 1;
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          onSegmentClick(index);
          return;
        default:
          return;
      }

      if (nextIndex !== null && nextIndex !== index) {
        setFocusedIndex(nextIndex);
        segmentRefs.current[nextIndex]?.focus();
      }
    },
    [total, onSegmentClick]
  );

  // Update focused index when current changes externally
  useEffect(() => {
    if (total <= 0) return;

    if (focusedIndex !== null && focusedIndex !== clampedCurrent) {
      // Only update focus if the container has focus
      if (
        containerRef.current?.contains(document.activeElement) &&
        segmentRefs.current[clampedCurrent]
      ) {
        setFocusedIndex(clampedCurrent);
      }
    }
  }, [clampedCurrent, focusedIndex, total]);

  // Reset refs array when total changes
  useEffect(() => {
    if (total > 0) {
      segmentRefs.current.length = total;
    }
  }, [total]);

  // Handle edge case: no segments - AFTER all hooks
  if (total <= 0) {
    return null;
  }

  // Determine segment state for styling
  const getSegmentState = (index: number): "filled" | "current" | "empty" => {
    if (index < clampedCurrent) return "filled";
    if (index === clampedCurrent) return "current";
    return "empty";
  };

  // Get tooltip ID for a segment
  const getTooltipId = (index: number) => `${tooltipIdPrefix}-tooltip-${index}`;

  // Render a single segment button
  const renderSegment = (index: number) => {
    const state = getSegmentState(index);
    const isHovered = hoveredIndex === index;
    const isFocused = focusedIndex === index;
    const isCurrent = index === clampedCurrent;
    const showTooltip = isHovered || isFocused;
    const tooltipId = getTooltipId(index);

    // Determine background color based on state
    let bgColorClass: string;
    switch (state) {
      case "filled":
        bgColorClass = "bg-brand-500";
        break;
      case "current":
        bgColorClass = "bg-brand-600";
        break;
      case "empty":
      default:
        bgColorClass = "bg-border";
    }

    return (
      <div key={index} className="relative flex-1">
        <button
          ref={(el) => {
            segmentRefs.current[index] = el;
          }}
          type="button"
          onClick={() => onSegmentClick(index)}
          onMouseEnter={() => setHoveredIndex(index)}
          onMouseLeave={() => setHoveredIndex(null)}
          onFocus={() => setFocusedIndex(index)}
          onBlur={() => setFocusedIndex(null)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          className={`h-1 w-full cursor-pointer rounded-sm transition-all duration-150 hover:h-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2 ${bgColorClass}`}
          aria-label={`Segment ${index + 1} of ${total}${isCurrent ? " (current)" : ""}`}
          aria-current={isCurrent ? "step" : undefined}
          aria-describedby={showTooltip ? tooltipId : undefined}
          tabIndex={isCurrent ? 0 : -1}
        />

        {/* Tooltip on hover/focus */}
        {showTooltip && (
          <div
            id={tooltipId}
            className="pointer-events-none absolute -top-8 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-brand-950 px-2 py-1 text-xs text-white shadow-lg"
            role="tooltip"
          >
            {index + 1}
          </div>
        )}
      </div>
    );
  };

  // Single segment case - show as full bar
  if (total === 1) {
    return (
      <div
        ref={containerRef}
        className={`flex flex-col gap-1.5 ${className}`}
        data-testid={testId}
        role="group"
        aria-label={label || "Progress: 1 of 1"}
      >
        <div className="flex h-1 w-full items-center gap-0.5">
          {renderSegment(0)}
        </div>
        {label && (
          <span className="text-center text-xs text-text-theme-muted">{label}</span>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`flex flex-col gap-1.5 ${className}`}
      data-testid={testId}
      role="group"
      aria-label={label || `Progress: ${clampedCurrent + 1} of ${total}`}
    >
      {/* Segment bar */}
      <div className="flex h-1 w-full items-center gap-0.5">
        {Array.from({ length: total }, (_, index) => renderSegment(index))}
      </div>

      {/* Label below bar */}
      {label && (
        <span className="text-center text-xs text-text-theme-muted">{label}</span>
      )}

      {/* Screen reader live region for position announcements */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {label || `Position ${clampedCurrent + 1} of ${total}`}
      </div>
    </div>
  );
}

export default ReplayProgressBar;
